import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { Col } from 'react-bootstrap';
import { trimLowerCase } from '../../../helpers/shared';
import { Input } from '../../shared/Input';
import {
  useDisabled,
  useTextLanguage
} from '../../../hooks/useShared';
import { useEdit } from '../../../hooks/useEdit'
import { useTextInput } from '../../../hooks/useInput'
import { useAutocompleteDocuments } from '../../../hooks/useAutocompleteDocuments';
import { maskedMinLengthFields, maskedPhones } from '../../../constants/form';
import { ThreeCircles } from 'react-loader-spinner';
import { StyledControl, StyledGroup, Required } from '../../shared/Input/styled';
import { Label } from '../../shared/simple/Label';

/**
 * Component text input
 * @component
 * @param {Object} field
 * @returns
 */
export const TextInput = ({ field, entryProps ='', isGroup='', isNewDesign=false }) => {
  const uniqueName = `${field.unique_name}__id__${field.id}`;
  const { description, languageText } = useTextLanguage();
  const { register, formState: { errors }, setValue } = useFormContext();
  const { type, maskLength, validate, isDocument, documentType } = useTextInput(field, uniqueName, isGroup);
  const { isDisabled, showLoadingInput } = useDisabled(field);
  const { colSize } = useEdit(field);
  useAutocompleteDocuments(field, uniqueName)

  return (
    <>
      {field && (
        <>
          {(colSize?.includes('col-before')) && (
            <Col xs={12} key={ `col_before_${field.id}` }></Col>
          )}
          <Col
            className={ colSize }
            data-module={ `module-${field.category?.field_category}` }
            data-name={ uniqueName }
            id={ `group_field_${field.id}` }
            key={ field.id }
            xs={12}
          >
            {showLoadingInput ? ( // ? Fake input, only dispayed briefly for 'description's of documents being fetched by the `document-conditions` endpoint
              <StyledGroup className="mb-3 w-100">
                <Label isNewDesign={isNewDesign}>
                  {field[description]}
                  {field.required && <Required className="text-danger">&nbsp;*</Required>}
                </Label>
                <div className="position-relative">
                  <StyledControl
                    $isNewDesign={isNewDesign}
                    aria-hidden="true"
                    className="w-100"
                    disabled
                    style={{ height: '38px' }}
                  />
                  <span
                    aria-label="Loading"
                    className="position-absolute top-50 start-50 translate-middle"
                    role="status"
                  >
                  <ThreeCircles
                    ariaLabel="three-circles-loading"
                    color={isNewDesign ? '#FFFFFF' : '#1D4ED8'}
                    height="20"
                    width="20"
                  />
                  </span>
                </div>
              </StyledGroup>
            ) : (
              <Input
                autoComplete="off"
                className="w-100"
                disabled={ isDisabled }
                field={ field }
                id={ trimLowerCase(field.id) }
                isRequiredInput={ field.required }
                label={ field[description] }
                message={ errors?.[uniqueName]?.message }
                name={ register(uniqueName).name }
                placeholder={ field[`placeholder${languageText}`] }
                register={ register(uniqueName, {
                  setValueAs: val => {
                    if (typeof val === 'string' && val !== 'hidden_field' && val !== 'hidden_rn' && (
                      maskedMinLengthFields.some((maskedField) => uniqueName.startsWith(maskedField)) ||
                      maskedPhones.some((maskedField) => uniqueName.startsWith(maskedField))
                    )
                    ) {
                      return val.replaceAll('-', '')?.replaceAll('_', '')
                                ?.replaceAll('(', '')?.replaceAll(')', '')
                    } else return val
                  }
                }) }
                setValue={ setValue }
                tooltipText={ field[`tooltip${languageText}`] }
                type={ type }
                entryProps={entryProps}
                isNewDesign={isNewDesign}
                maskLength={maskLength}
                validate={validate}
                isDocument={isDocument}
                documentType={documentType}
              />
            )}
          </Col>
        </>
      )}
    </>
  );
};

TextInput.propTypes = {
  field: PropTypes.object.isRequired
};
